import { Component, ChangeDetectionStrategy, Input, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { localStorageService } from 'src/app/services/localstorage.service';
import { MainService } from 'src/app/services/main.service';
import { SharedService } from 'src/app/services/shared.service';

import { environment } from 'src/environments/environment';
import { SubscriptionSettingsService } from 'src/app/pages/components/subscription/subscription-settings.service';
import { NgClass, NgStyle } from '@angular/common';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { TerminalFlagsService } from 'src/app/pages/components/fm-terminal/terminal-flags.service';

@Component({
    selector: 'app-sidemenu',
    templateUrl: './sidemenu.component.html',
    styleUrl: './sidemenu.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink, NgClass, NgStyle, RouterLinkActive, TooltipDirective]
})
export class SidemenuComponent {
  public environment = environment;
  /** Every menu the server sent and this business may see. */
  menuList: any = [];

  private readonly terminalFlags = inject(TerminalFlagsService);

  /**
   * The menus actually drawn.
   *
   * `show_terminal_menu` is the server's whole rule for this feature in one
   * boolean — brick-and-mortar, the flow enabled, onboarding done, identity
   * verified — and the menu it sends does not account for it, so the row
   * arrives for businesses the rule refuses. Filtered here rather than hidden
   * with CSS so everything reading the menu agrees: the first-item redirect
   * below, and sideMenus$, which other screens read.
   *
   * Read on each pass rather than built once: the menu is assembled from the
   * login response and the profile carrying the flag can land either side of
   * it, so a list filtered once could be filtered before the answer arrived.
   *
   * Hiding the row is only half of it — the URL still exists, and a bookmark
   * or a typed address would walk straight past this. `canOpenTerminalGuard`
   * asks the same question of the route; the two are deliberately one flag,
   * not two rules that could drift apart.
   */
  get visibleMenuList(): any[] {
    if (this.terminalFlags.showTerminalMenu()) { return this.menuList; }
    return this.menuList.filter((item: any) => !isTerminalMenu(item));
  }
  currentUrl: string = '';  // Store the current route
  activeMenuStates: { [path: string]: boolean } = {};  // Store active states for menu items
  userProfile: any;
  agentData:any;
  dispute_count:any;
  disputes_data:any;
  canHide:boolean=true;
  isCollapsed:boolean = false;  // desktop side-menu collapsed (icon-only) state
  // Set on side-menus rendered inside a page (e.g. the new-financing flow) rather than by
  // LayoutComponent. Those instances are display-only and must not perform the post-login
  // landing redirect below, which would bounce an agent straight back out of the page.
  @Input() embedded = false;

  /* Subscriptions are switched off per business on doctor_settings, and the
     whole section goes with them — there is nothing to manage when nothing can
     be sold. Read rather than asked for: the settings are already on
     doctorSettings$ from the profile bootstrap. */
  private readonly subSettings = inject(SubscriptionSettingsService);

  constructor(private _router: Router, private _main: MainService, public _shared: SharedService,private _local:localStorageService) {
    this.userProfile = this._local.customGetItem('userProfileData');
  }

  /**
   * Whether a menu row is the Subscription section.
   *
   * Matched on the enum and the path rather than one id, because the row is a
   * database record (`fm_static.enterprise_menu_items`) and this file cannot
   * see which id it was given.
   */
  private isSubscriptionMenu(item: any): boolean {
    const enumName = String(item?.menu_enum || '').toUpperCase();
    const path = String(item?.path || '').toLowerCase();
    return enumName.includes('SUBSCRIPTION') || path.includes('subscription');
  }

  ngOnInit() {
    this._shared.doctorProfile$.subscribe(response => {
      this.userProfile = response;
    })

    /* The settings land after the menu does — main.service pushes
       authenticateApiResponse$ and only then assigns the profile that fills
       doctorSettings$ — so the section is taken back off here rather than
       filtered out at build time, which would always run a beat too early. */
    this._shared.doctorSettings$.subscribe(() => {
      if (!this.menuList.length || this.subSettings.enabled) { return; }
      const remaining = this.menuList.filter((item: any) => !this.isSubscriptionMenu(item));
      if (remaining.length === this.menuList.length) { return; }
      this.menuList = remaining;
      this.updateMenuActiveStates();
      this._main._shared.sideMenus$.next(this.menuList);
    })
    this._main._shared.authenticateApiResponse$.subscribe((allLoginDataResponse: any) => {
      if (allLoginDataResponse.menu && allLoginDataResponse.menu.length) {
        this.menuList = [];
        this.disputes_data = allLoginDataResponse.disputes_data

        // fetch all parent menus with only path 0 . // 0 here means to display menu and 1 means do not display but is a route
        const parentMenus = allLoginDataResponse.menu.filter((menu: any) => !menu.parent_id && menu.only_path == 0);
        parentMenus.map((item: any) => {
          if (!item.show_to_business) {
            return;
          }
          if (this.isSubscriptionMenu(item) && !this.subSettings.enabled) {
            return;
          }
          if (item.submenu && item.submenu.length) {
            const submenus = item.submenu.filter((sub_menu: any) => sub_menu.only_path == 0);
            const menuItem = {...item};
            menuItem.submenu = submenus;
            this.menuList.push(menuItem);
          } else {
            this.menuList.push(item);
          }
        })

        this.currentUrl = this._router.url;
        this.updateMenuActiveStates();
        console.log(this.menuList);

if(this.userProfile.role_id == 5){
          this._shared.hasFinanceCustomer = this.menuList.some(
            (item: any) => item.menu_enum === 'MANAGE_PAYMENT_PLANS_CONTRACT_LIST'
          );
        }

        const visible = this.visibleMenuList;
        if (this.userProfile.role_id == 5 && !this.embedded && visible.length) {
          this._router.navigate([visible[0].path]);
        }
        this._main._shared.sideMenus$.next(visible);
      }
    });

    this.userProfile = this._local.customGetItem('userProfileData');
    this.agentData =this._local.customGetItem('userDoctorData');
    this._shared.dispute_count = this.userProfile.doc_count;

    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.currentUrl = this._router.url;
      this.updateMenuActiveStates();
    });
  }

  private updateMenuActiveStates(): void {
    // Reset all menu states
    this.activeMenuStates = {};

    // Compute active state for each menu
    this.menuList.forEach((menu: any) => {
      // Check if the current URL starts with the menu path
      this.activeMenuStates[menu.path] = this.currentUrl.startsWith(menu.path);

      // If there are submenus, compute their active state
      if (menu.submenu) {
        menu.submenu.forEach((sub_menu: any) => {
          this.activeMenuStates[sub_menu.path] = this.currentUrl.startsWith(sub_menu.path);
        });
      }
    });
  }

  toggleMenu(selectedMenu: any) {
    if (selectedMenu.submenu && selectedMenu.submenu.length) {
      selectedMenu.isOpen = !selectedMenu.isOpen;
    } else {
      this._router.navigate([selectedMenu.path]);
    }
    // if (menu === selectedMenu) {
    // } else {
    //   // menu.isOpen = false;
    // }
    // this.menuList.forEach((menu: any) => {
    // });
  }

  routeClick() {
    // this._shared.search_emitter.subscribe(data => {
    //   if(data && data.length) {
    //     this._shared.search_emitter.next('');
    //     window.location.reload();
    //   }
    // })
  }


  closeNavMenu() {
    const sidenav = document.getElementById('mySidenav');
    if (sidenav) {
      sidenav.style.transform = 'translate(-100%)';
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    // collapse any open submenus so the icon-only rail stays clean
    if (this.isCollapsed) {
      this.menuList.forEach((menu: any) => (menu.isOpen = false));
    }
  }
}

/**
 * Whether a menu row is the terminal's.
 *
 * Matched on the route rather than on a name: the label is "FM Terminal"
 * today and is a server-side string someone may reword, while the path is
 * what the feature is actually mounted at.
 */
function isTerminalMenu(item: any): boolean {
  const path = String(item?.path || '');
  return /(^|\/)terminal(\/|$)/.test(path) || String(item?.menu_enum || '').includes('TERMINAL');
}
